import React, { useEffect, useContext, useRef, useState } from 'react';
import { Badge, Grid, Button } from '@mui/material/';
import { Link, useLocation } from 'react-router-dom';
import useRoutes from '../../routes/Routes';
import { confirmAlert } from 'react-confirm-alert';
import { AppContext } from '../../context/Context';
import { SocketContext } from '../../context/SocketContext';
import 'react-confirm-alert/src/react-confirm-alert.css';
import '../../assets/css/buttons.css';
import { useTranslation } from 'react-i18next';
import toast, { Toaster } from 'react-hot-toast';
import { statusTone } from '../../utils/errorTicketStatus';
import {
  InfoOutlined as InfoOutlinedIcon,
  LockPerson as LockPersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassTop as HourglassTopIcon,
} from '@mui/icons-material/';

const TICKET_ICON = {
  open: InfoOutlinedIcon,
  in_progress: HourglassTopIcon,
  approved: CheckCircleIcon,
  rejected: CancelIcon,
};

// Toast in the ticket's status colour, never the generic success green
const ticketToast = (status, message) => {
  const tone = statusTone(status);
  const Icon = TICKET_ICON[status] ?? TICKET_ICON.open;

  toast(message, {
    duration: 9000,
    icon: <Icon style={{ color: tone.color, fontSize: '30px' }} />,
    style: { borderLeft: `6px solid ${tone.color}` },
  });
};

function MenuButtons() {
  const Routes = useRoutes();
  const { t } = useTranslation();
  const location = useLocation();
  const inSalgs = location.pathname === '/salgs';
  const { socket } = useContext(SocketContext);
  const { socketPW } = useContext(SocketContext);
  const { socketReport } = useContext(SocketContext);

  const {
    updateLockUserInfo,
    userInfo,
    parentInfo,
    updateShowModalUnlockUser,
    hall,
    updateShowSpecialGames,
    transactions,
    updateShowModalEndShift,
    currentUser,
    updateShowModalEditUserLimits,
    enviroment,
    userLimits,
    updateSpecialGameInfo,
    printer,
    updateIsLoadingScreen,
    session,
    operator,
    isAdmin,
    updateShowModalCashMovement,
    updateShowModalUserLimitStatus,
    updateShowModalTerminals,
    updateShowModalErrorTicket,
    updateTerminals,
  } = useContext(AppContext);

  useEffect(() => {
    const handleOpenCashier = (data) => {
      updateIsLoadingScreen(false);
      if (!data) {
        toast.error(t('error.error'));
        return;
      }
    };

    const handleCheckLockUserStatus = (data) => {
      updateIsLoadingScreen(false);
      if (!data) {
        toast.error(t('error.error'));
        return;
      }
      updateLockUserInfo(data);
      updateShowModalUnlockUser(true);
    };

    const handleGetNetworkMap = (data) => {
      console.log('Received terminals data:', data);
      updateIsLoadingScreen(false);
      if (!data) {
        toast.error(t('error.error'));
        return;
      }
      updateTerminals(data);
      updateShowModalTerminals(true);
    };

    socket.on('openCashier', handleOpenCashier);
    socket.on('checkLockUserStatus', handleCheckLockUserStatus);
    socket.on('getNetworkMap', handleGetNetworkMap);
    return () => {
      socket.off('openCashier', handleOpenCashier);
      socket.off('checkLockUserStatus', handleCheckLockUserStatus);
      socket.off('getNetworkMap', handleGetNetworkMap);
    };
  }, [socket]);

  // Ticket notices live here (always mounted); pendingTickets counts the ones not closed
  const [pendingTickets, setPendingTickets] = useState(0);
  // A status change is a colour change; the mark keeps it until the modal is opened
  const [unseenTone, setUnseenTone] = useState(null);
  const ticketStatusRef = useRef(null);
  // What was filed at this till: the modal already says so, no second notice
  const filedHereRef = useRef(new Set());

  useEffect(() => {
    if (!socket || !socketReport || !hall) return;

    // statusAt changes on any office move, so a note alone counts as a change
    const stamp = (ticket) => `${ticket.status}|${ticket.statusAt ?? ''}`;

    const handleList = (data) => {
      const tickets = Array.isArray(data?.tickets) ? data.tickets : [];
      setPendingTickets(
        tickets.filter((x) => x.status === 'open' || x.status === 'in_progress')
          .length
      );

      const previous = ticketStatusRef.current;
      // The first answer is the state as it already was, not something that moved
      if (previous) {
        const moved = tickets.filter((ticket) => {
          const before = previous.get(ticket.id);
          if (before) return before !== stamp(ticket);
          // Filed while we were looking: ours is announced by the modal itself
          return !filedHereRef.current.has(ticket.id);
        });

        // Toast oldest first so the newest ends on top
        [...moved].reverse().forEach((ticket) => {
          const status = t(statusTone(ticket.status).labelKey);
          const message = previous.has(ticket.id)
            ? ticket.statusNote
              ? t('errorTicket.statusChangedNote', {
                  id: ticket.id,
                  status,
                  note: ticket.statusNote,
                })
              : t('errorTicket.statusChanged', { id: ticket.id, status })
            : t('errorTicket.filedElsewhere', {
                id: ticket.id,
                title: ticket.title,
              });

          ticketToast(ticket.status, message);
        });

        // One mark for the whole batch: the colour of the notice sitting on top
        if (moved.length) setUnseenTone(statusTone(moved[0].status).color);
      }

      ticketStatusRef.current = new Map(
        tickets.map((ticket) => [ticket.id, stamp(ticket)])
      );
    };

    const ask = () => socket.emit('listErrorTickets', { bingoName: hall });

    // Broadcast to every till, same as PowerBingo's: keep only our own hall
    const handleChanged = (data) => {
      if (data?.bingoName && data.bingoName !== hall) return;
      ask();
    };

    const handleFiled = (data) => {
      if (data?.ok && data.ticket?.id) filedHereRef.current.add(data.ticket.id);
    };

    socket.on('listErrorTickets', handleList);
    socket.on('newErrorTicket', handleFiled);
    // Filed at another till of this hall
    socket.on('errorTicketChanged', handleChanged);
    // Moved on the report side, which has no socket to this backend
    socketReport.on('errorTicketChanged', handleChanged);
    // Whatever moved while the line was down is told when it comes back
    socket.on('connect', ask);
    ask();

    return () => {
      socket.off('listErrorTickets', handleList);
      socket.off('newErrorTicket', handleFiled);
      socket.off('errorTicketChanged', handleChanged);
      socketReport.off('errorTicketChanged', handleChanged);
      socket.off('connect', ask);
      // The next hall starts from its own state, not from this one's
      ticketStatusRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, socketReport, hall]);

  useEffect(() => {
    const handleCheckIsSpecialGameOpen = (data) => {
      if (!data || (!data.isLykkehjul && !data.isLuckyDay)) {
        modalNoSpecialGames();
        //updateSpecialGameInfo(false);
        return;
      }
      if (data.isLykkehjul === true || data.isLuckyDay === true) {
        updateShowSpecialGames(true);
        updateSpecialGameInfo(data);
      }
    };

    const handleNewSpecialGame = (data) => {
      if (data.bingoName && data.bingoName === hall) {
        //if (data.bingoName && data.bingoName === 'Bingo 1 Demo PROD') {
        toast.success(t('header.specialGameAvailable'), {
          duration: 7000,
        });
      }
    };

    socketPW.on('checkIsSpecialGameOpen', handleCheckIsSpecialGameOpen);
    socketPW.on('newSpecialGame', handleNewSpecialGame);

    return () => {
      socketPW.off('checkIsSpecialGameOpen', handleCheckIsSpecialGameOpen);
      socketPW.off('newSpecialGame', handleNewSpecialGame);
    };
  }, [socketPW]);

  const modalNoSpecialGames = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="react-confirm-alert-body" style={{ width: '600px' }}>
          <h1>{t('header.specialGameMessageError')}</h1>
          <div className="react-confirm-alert-button-group">
            <button
              className="btn-custom-yes"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
              onClick={() => {
                onClose();
              }}
            >
              <CheckCircleIcon style={{ fontSize: '1.2em' }} />
              {t('sales.confirm')}
            </button>
          </div>
        </div>
      ),
    });
  };

  const checkSpecialGames = () => {
    if (transactions.length > 0) {
      toast.error(t('error.errorNavigateTransactions'), {
        duration: 5000,
      });
      return;
    }
    socketPW.emit('checkIsSpecialGameOpen', 'checkIsSpecialGameOpen');
  };

  const checkTrans = (event) => {
    if (transactions.length > 0) {
      event.preventDefault();
      toast.error(t('error.errorNavigateTransactions'), {
        duration: 5000,
      });
      return;
    }
    if (currentUser !== '') {
      event.preventDefault();
      toast.error(t('error.errorNavigateTransactions'), {
        duration: 5000,
      });
      return;
    }
  };

  const modalEndShift = () => {
    if (transactions.length > 0) {
      toast.error(t('error.errorNavigateTransactions'), {
        duration: 5000,
      });
      return;
    }
    if (currentUser !== '') {
      toast.error(t('error.errorNavigateTransactions'), {
        duration: 5000,
      });
      return;
    }
    updateIsLoadingScreen(true);
    updateShowModalEndShift(true);
  };

  const handleUserLimits = () => {
    updateShowModalEditUserLimits(true);
  };

  const handlePrintCards = () => {
    socket.emit('printCards', {
      bingoName: hall,
      printer: printer,
    });
  };

  const handleOpenCashier = () => {
    updateIsLoadingScreen(true);
    socket.emit('openCashier', {
      bingoName: hall,
      operator: operator,
      sessionID: session,
      printer: printer,
    });
  };

  const handleShowModalRefund = () => {
    updateShowModalCashMovement(true);
  };

  const handleTerminals = () => {
    updateIsLoadingScreen(true);
    socket.emit('getNetworkMap', {
      bingoName: hall,
    });
  };

  const handleCheckLockUserStatus = () => {
    updateIsLoadingScreen(true);
    socket.emit('checkLockUserStatus', {
      userID: parentInfo ? parentInfo.id : userInfo.id,
      bingoName: hall,
    });
  };

  const handleMysteryWinners = () => {
    if (transactions.length > 0) {
      toast.error(t('error.errorNavigateTransactions'), {
        duration: 5000,
      });
      return;
    }
    updateIsLoadingScreen(true);
    socket.emit('checkMysteryWinners', {});
  };

  return (
    <Grid
      container
      direction="row"
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignContent: 'flex-start',
        alignItems: 'flex-start',
        height: '100%',
        // Tighter rows when the limits panel shows, so its 110px still fit
        rowGap: userLimits?.daily?.limit ? '15px' : '22px',
        borderBottom: '6px solid black',
      }}
    >
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          style: {
            zIndex: '9999999 !important',
            fontSize: '24px',
          },
        }}
        containerStyle={{
          zIndex: 99999999, // Ensure this value is higher than the modal's z-index
        }}
      />
      {Routes.map((route, index) => {
        if (
          Routes[index].name === 'login' ||
          Routes[index].name === 'login-kiosk'
        ) {
          return;
        }
        return (
          <Grid
            item
            xs={6}
            sx={{
              paddingTop: '0px !important',
              paddingLeft: '0px !important!',
              display: 'flex',
              justifyContent: 'center',
              alignContent: 'center',
              alignItem: 'center',
            }}
            key={index}
          >
            {Routes[index].name === t('header.sales') ||
            Routes[index].name === t('header.lastTransactions') ||
            Routes[index].name === t('header.lastCardTransactions') ||
            Routes[index].name === t('header.ticketHistoric') ||
            Routes[index].name === t('header.openTickets') ||
            Routes[index].name === t('header.ticketList') ||
            Routes[index].name === t('header.gameplan') ||
            /*Routes[index].name === t('header.endShift') ||*/

            Routes[index].name === t('header.recent') ? (
              <Link
                to={Routes[index].route}
                onClick={(event) => {
                  checkTrans(event);
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    height: 78,
                    width: 88,
                    padding: '7px !important',
                    background:
                      'linear-gradient(160deg, #9dafbd 0%, #6d8190 100%)',
                    boxShadow:
                      '0 4px 12px rgba(136,156,171,0.2), inset 0 1px 0 rgba(255,255,255,0.14)',
                    borderRadius: '12px',
                    transition: 'transform 80ms ease, box-shadow 80ms ease',
                    '&:active': {
                      transform: 'translateY(3px)',
                      boxShadow:
                        '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
                    },
                    fontWeight: 'bold',
                    color: 'white',
                    fontSize: '12px',
                  }}
                >
                  {Routes[index].name}
                </Button>
              </Link>
            ) : (
              <Button
                variant="contained"
                sx={{
                  height: 78,
                  width: 88,
                  padding: '7px !important',
                  backgroundColor: 'grey',
                  fontWeight: 'bold',
                  color: 'white',
                  fontSize: '12px',
                  opacity: '0.25',
                }}
              >
                {Routes[index].name}
              </Button>
            )}
          </Grid>
        );
      })}
      <Grid
        item
        xs={6}
        sx={{
          paddingTop: '0px !important',
          paddingLeft: '0px !important!',
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          alignItem: 'center',
        }}
      >
        <Button
          variant="contained"
          sx={{
            height: 78,
            width: 88,
            padding: '10px !important',
            background: 'linear-gradient(160deg, #9dafbd 0%, #6d8190 100%)',
            boxShadow:
              '0 4px 12px rgba(136,156,171,0.2), inset 0 1px 0 rgba(255,255,255,0.14)',
            borderRadius: '12px',
            transition: 'transform 80ms ease, box-shadow 80ms ease',
            '&:active': {
              transform: 'translateY(3px)',
              boxShadow:
                '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
            },
            fontWeight: 'bold',
            color: 'white',
            fontSize: '12px',
            opacity: inSalgs ? '1' : '0.25',
          }}
          onClick={() => {
            if (!inSalgs) return;
            modalEndShift();
          }}
        >
          {t('header.endShift')}
        </Button>
      </Grid>

      <Grid
        item
        xs={6}
        sx={{
          paddingTop: '0px !important',
          paddingLeft: '0px !important!',
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          alignItem: 'center',
        }}
      >
        <Button
          variant="contained"
          sx={{
            height: 78,
            width: 88,
            padding: '10px !important',
            background: 'linear-gradient(160deg, #9dafbd 0%, #6d8190 100%)',
            boxShadow:
              '0 4px 12px rgba(136,156,171,0.2), inset 0 1px 0 rgba(255,255,255,0.14)',
            borderRadius: '12px',
            transition: 'transform 80ms ease, box-shadow 80ms ease',
            '&:active': {
              transform: 'translateY(3px)',
              boxShadow:
                '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
            },
            fontWeight: 'bold',
            color: 'white',
            fontSize: '12px',
            opacity: inSalgs ? '1' : '0.25',
          }}
          onClick={() => {
            if (!inSalgs) return;
            checkSpecialGames();
          }}
        >
          {t('header.specialGame')}
        </Button>
      </Grid>

      <Grid
        item
        xs={6}
        sx={{
          paddingTop: '0px !important',
          paddingLeft: '0px !important!',
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          alignItem: 'center',
        }}
      >
        <Button
          variant="contained"
          sx={{
            height: 78,
            width: 88,
            padding: '10px !important',
            background: 'linear-gradient(160deg, #9dafbd 0%, #6d8190 100%)',
            boxShadow:
              '0 4px 12px rgba(136,156,171,0.2), inset 0 1px 0 rgba(255,255,255,0.14)',
            borderRadius: '12px',
            transition: 'transform 80ms ease, box-shadow 80ms ease',
            '&:active': {
              transform: 'translateY(3px)',
              boxShadow:
                '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
            },
            fontWeight: 'bold',
            color: 'white',
            fontSize: '12px',
            opacity: inSalgs ? '1' : '0.25',
          }}
          onClick={() => {
            if (!inSalgs) return;
            handleMysteryWinners();
          }}
        >
          {t('header.mysteryWinners')}
        </Button>
      </Grid>

      <Grid
        item
        xs={6}
        sx={{
          paddingTop: '0px !important',
          paddingLeft: '0px !important!',
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          alignItem: 'center',
        }}
      >
        <Button
          variant="contained"
          sx={{
            height: 78,
            width: 88,
            padding: '10px !important',
            background: 'linear-gradient(160deg, #9dafbd 0%, #6d8190 100%)',
            boxShadow:
              '0 4px 12px rgba(136,156,171,0.2), inset 0 1px 0 rgba(255,255,255,0.14)',
            borderRadius: '12px',
            transition: 'transform 80ms ease, box-shadow 80ms ease',
            '&:active': {
              transform: 'translateY(3px)',
              boxShadow:
                '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
            },
            fontWeight: 'bold',
            color: 'white',
            fontSize: '12px',
          }}
          onClick={() => {
            handleOpenCashier();
          }}
        >
          {t('header.openCashier')}
        </Button>
      </Grid>

      {/* No isAdmin gate, and no player gate: moving the till is a cashier's
          job, and taking cash out or putting it in has no player behind it */}
      <Grid
        item
        xs={6}
        sx={{
          paddingTop: '0px !important',
          paddingLeft: '0px !important!',
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          alignItem: 'center',
        }}
      >
        <Button
          variant="contained"
          sx={{
            height: 78,
            width: 88,
            padding: '10px !important',
            background: 'linear-gradient(160deg, #9dafbd 0%, #6d8190 100%)',
            boxShadow:
              '0 4px 12px rgba(136,156,171,0.2), inset 0 1px 0 rgba(255,255,255,0.14)',
            borderRadius: '12px',
            transition: 'transform 80ms ease, box-shadow 80ms ease',
            '&:active': {
              transform: 'translateY(3px)',
              boxShadow:
                '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
            },
            fontWeight: 'bold',
            color: 'white',
            fontSize: '12px',
          }}
          onClick={() => {
            handleShowModalRefund();
          }}
        >
          {t('header.cashMovement')}
        </Button>
      </Grid>

      <Grid
        item
        xs={6}
        sx={{
          paddingTop: '0px !important',
          paddingLeft: '0px !important!',
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          alignItem: 'center',
        }}
      >
        <Button
          variant="contained"
          sx={{
            height: 78,
            width: 88,
            padding: '10px !important',
            background: 'linear-gradient(160deg, #9dafbd 0%, #6d8190 100%)',
            boxShadow:
              '0 4px 12px rgba(136,156,171,0.2), inset 0 1px 0 rgba(255,255,255,0.14)',
            borderRadius: '12px',
            transition: 'transform 80ms ease, box-shadow 80ms ease',
            '&:active': {
              transform: 'translateY(3px)',
              boxShadow:
                '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
            },
            fontWeight: 'bold',
            color: 'white',
            fontSize: '12px',
            opacity: inSalgs ? '1' : '0.25',
          }}
          onClick={() => {
            if (!inSalgs) return;
            handleTerminals();
          }}
        >
          {t('header.terminals')}
        </Button>
      </Grid>

      <Grid
        item
        xs={6}
        sx={{
          paddingTop: '0px !important',
          paddingLeft: '0px !important!',
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          alignItem: 'center',
        }}
      >
        {/* Number = tickets not closed, colour = last unseen change */}
        <Badge
          badgeContent={pendingTickets}
          // Zero with a colour still says something moved: the last one was closed
          showZero={Boolean(unseenTone)}
          sx={{
            // The mark goes out with its button, it is not a live one off /salgs
            opacity: inSalgs ? '1' : '0.25',
            '& .MuiBadge-badge': {
              backgroundColor: unseenTone || '#fcad89',
              color: '#151d3b',
              fontWeight: 700,
              fontSize: '15px',
              height: 28,
              minWidth: 28,
              borderRadius: '14px',
              // The ring is the header's own background, not the modals' panel
              border: '2px solid #08141e',
              transition: 'background-color 200ms ease',
            },
          }}
        >
          <Button
            variant="contained"
            sx={{
              height: 78,
              width: 88,
              padding: '10px !important',
              background: 'linear-gradient(160deg, #9dafbd 0%, #6d8190 100%)',
              boxShadow:
                '0 4px 12px rgba(136,156,171,0.2), inset 0 1px 0 rgba(255,255,255,0.14)',
              borderRadius: '12px',
              transition: 'transform 80ms ease, box-shadow 80ms ease',
              '&:active': {
                transform: 'translateY(3px)',
                boxShadow:
                  '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
              },
              fontWeight: 'bold',
              color: 'white',
              fontSize: '12px',
            }}
            onClick={() => {
              if (!inSalgs) return;
              setUnseenTone(null);
              updateShowModalErrorTicket(true);
            }}
          >
            {t('header.errorTicket')}
          </Button>
        </Badge>
      </Grid>

      {userLimits && userLimits.daily && userLimits.daily.limit ? (
        <Grid
          item
          xs={10}
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignContent: 'center',
            alignItem: 'center',
            backgroundColor: 'white',
            height: '110px',
            borderTopRightRadius: '25px',
            borderBottomRightRadius: '25px',
            padding: '10px 25px',
            flexDirection: 'column',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: '15px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => {
              updateShowModalUserLimitStatus(true);
            }}
          >
            <InfoOutlinedIcon sx={{ color: 'black', fontSize: '40px' }} />
          </div>

          <div
            style={{
              position: 'absolute',
              right: '15px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => {
              handleCheckLockUserStatus();
            }}
          >
            <LockPersonIcon sx={{ color: 'black', fontSize: '40px' }} />
          </div>

          <p
            style={{
              color:
                userLimits.daily.limit >
                userLimits.daily.cashIn - userLimits.daily.cashOut
                  ? 'black'
                  : '#fc86c1',
              fontSize: '12px',
              marginBottom: '0px',
            }}
          >
            {t('sales.limitDaily')}:<br />
            <span style={{ fontSize: '18px' }}>
              {userLimits.daily.cashIn - userLimits.daily.cashOut}
            </span>{' '}
            /{' '}
            <span style={{ fontSize: '18px' }}>
              {userLimits.daily.limit > 0 && userLimits.daily.limit}
            </span>
          </p>
          <p
            style={{
              color:
                userLimits.monthly.limit >
                userLimits.monthly.totalSpent - userLimits.monthly.rollover
                  ? 'black'
                  : '#fc86c1',
              fontSize: '12px',
            }}
          >
            {t('sales.limitMonthly')}:<br />{' '}
            <span
              style={{
                fontSize: '18px',
              }}
            >
              {userLimits.monthly.totalSpent - userLimits.monthly.rollover}
            </span>{' '}
            /{' '}
            <span
              style={{
                fontSize: '18px',
              }}
            >
              {userLimits.monthly.limit > 0 && userLimits.monthly.limit}
            </span>
          </p>
        </Grid>
      ) : null}
    </Grid>
  );
}

export default MenuButtons;
